import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ProductShowcase, barShowcaseItems } from "./productShowCase";
import { Gallery, barProducts } from "./Gallery";
import { CuratedCollections, barCollections } from "./CuratedCollections";
import Contact from "./Contact";
import ScrollingSection from "./ScrollingSection";
import ScrollToTop from "./ScroollToTop";

const slides = [
  {
    id: 1,
    image: 'https://images.pexels.com/photos/460537/pexels-photo-460537.jpeg?auto=compress&cs=tinysrgb&w=1920',
    title: 'Modern Bar Lounge',
    subtitle: 'Stylish Comfort',
    description: 'Upgrade your bar experience with elegant and modern furniture that blends form with function.'
  },
  {
    id: 2,
    image: 'https://images.pexels.com/photos/1283219/pexels-photo-1283219.jpeg?auto=compress&cs=tinysrgb&w=1920',
    title: 'Luxury Bar Setup',
    subtitle: 'Elegant Design',
    description: 'Explore handcrafted bar furniture that exudes luxury and complements your space.'
  },
  {
    id: 3,
    image: 'https://images.pexels.com/photos/260922/pexels-photo-260922.jpeg?auto=compress&cs=tinysrgb&w=1920',
    title: 'Minimalist Bar Collection',
    subtitle: 'Refined Aesthetics',
    description: 'Discover minimalist furniture tailored for modern bar environments with sleek finishes.'
  },
  {
    id: 4,
    image: 'https://images.pexels.com/photos/941861/pexels-photo-941861.jpeg?auto=compress&cs=tinysrgb&w=1920',
    title: 'Cozy Bar Nook',
    subtitle: 'Welcoming Warmth',
    description: 'Bring warmth and character to your bar with inviting designs and premium materials.'
  }
];

// Contact Modal Component
const ContactModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white shadow-3xl w-full max-w-2xl max-h-[90vh] sm:max-h-[95vh] overflow-y-auto relative rounded-lg">
        <button
          className="absolute top-2 right-3 sm:top-3 sm:right-4 text-gray-500 hover:text-gray-700 text-2xl sm:text-3xl font-bold z-[9999]"
          aria-label="Close modal"
          onClick={onClose}
        >
          &times;
        </button>
        <Contact />
      </div>
    </div>
  );
};

function Bar() {
  const [showModal, setShowModal] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  // Preload hero images
  useEffect(() => {
    slides.forEach((slide) => {
      const img = new Image();
      img.src = slide.image;
    });
  }, []);

  const openModal = () => setShowModal(true);
  const closeModal = () => setShowModal(false);

  useEffect(() => {
    if (isAutoPlaying) {
      const interval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % slides.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [isAutoPlaying]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 3000);
  };

  return (
    <div className="animate-slideUp">
      <ScrollToTop />

      {/* Hero Image Slider */}
      <div className="relative w-full h-[50vh] sm:h-[60vh] md:h-[70vh] lg:h-[80vh] overflow-hidden">
        {slides.map((slide, index) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${index === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'
              }`}
          >
            <img
              src={slide.image}
              alt={slide.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/30 to-transparent" />
          </div>
        ))}
        <div className="absolute inset-0 flex items-center justify-start px-4 sm:px-6 lg:px-12 z-20">
          <div className="max-w-2xl text-white">
            <p className="text-xs sm:text-sm lg:text-base font-medium tracking-wider uppercase mb-2 text-yellow-400">
              {slides[currentSlide].subtitle}
            </p>
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold leading-tight mb-4 sm:mb-6">
              {slides[currentSlide].title}
            </h1>
            <p className="text-sm sm:text-base md:text-lg lg:text-xl leading-relaxed mb-6 sm:mb-8 text-gray-100">
              {slides[currentSlide].description}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <Link
                to="/bar/all"
                className="bg-yellow-500 hover:bg-yellow-600 text-white text-center no-underline font-semibold py-3 px-6 sm:py-4 sm:px-8 text-sm sm:text-base rounded-lg transition-all duration-300 transform hover:scale-105 hover:shadow-lg"
              >
                Explore the Collection
              </Link>
              <button
                onClick={openModal}
                className="border-2 border-white hover:bg-white hover:text-gray-900 font-semibold py-3 px-6 sm:py-4 sm:px-8 text-sm sm:text-base rounded-lg transition-all duration-300 transform hover:scale-105"
              >
                Book Consultation
              </button>
            </div>
          </div>
        </div>
        <div className="absolute bottom-4 sm:bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-2 sm:space-x-3 z-30">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-2 h-2 sm:w-3 sm:h-3 rounded-full transition-all duration-300 ${index === currentSlide
                ? 'bg-yellow-500 scale-125'
                : 'bg-white bg-opacity-50 hover:bg-opacity-80'
                }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>

      {/* Product Showcase */}
      <ProductShowcase
        title="Bar Furniture Collections"
        items={barShowcaseItems}
      />

      {/* Gallery Section */}
      <section id="gallery" className="bg-gray-50 py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-12 min-h-[700px] sm:min-h-[900px]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-3 sm:mb-4 text-gray-900">The Bar Collection</h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-base sm:text-lg px-4">
              Explore our curated selection of bar furniture, from handcrafted stools to sophisticated storage solutions.
            </p>
          </div>

          <div className="min-h-[500px] sm:min-h-[600px]">
            <Gallery
              products={barProducts}
              categories={['Bar Stools', 'Bar Counters', 'Bar Cabinets', 'Storage & Display']}
              autoCycle={true}
            />
          </div>
        </div>
      </section>

      {/* Custom Solutions */}
      <section>
        <ScrollingSection
          sectionContent={
            <section className="relative z-10 bg-black/80 p-4 sm:p-6 md:p-8 lg:p-12 xl:p-16 w-full min-h-screen flex items-center justify-center">
              <div className="max-w-6xl mx-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 items-center">
                  <div className="space-y-4 sm:space-y-6 text-left">
                    <h2 className="text-2xl sm:text-3xl md:text-4xl font-serif text-white leading-tight">Custom Bar Furniture Solutions</h2>
                    <p className="text-gray-300 text-sm sm:text-base md:text-lg leading-relaxed">
                      We offer tailor-made bar furniture solutions that perfectly match your brand’s style and space. From custom design services to space planning and material/finish selection, we provide end-to-end services for your commercial needs.
                    </p>
                    <ul className="list-disc list-inside text-gray-300 text-sm sm:text-base md:text-lg space-y-2">
                      <li>Custom Design Services</li>
                      <li>Space Planning</li>
                      <li>Material & Finish Selection</li>
                    </ul>
                    <button className="mt-6 sm:mt-8 inline-block bg-gray-600 text-white px-6 sm:px-8 py-2 sm:py-3 rounded-full text-sm sm:text-base md:text-lg font-semibold hover:bg-gray-700 transition-colors duration-300 shadow-lg hover:shadow-xl">
                      Explore More
                    </button>
                  </div>
                  <div className="rounded-lg shadow-lg">
                    <iframe
                      width="100%"
                      className="rounded-lg h-48 sm:h-64 md:h-80 lg:h-96"
                      src="https://www.youtube.com/embed/HcqrTH-warU?autoplay=1&mute=1&start=10"
                      title="YouTube video player"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      referrerPolicy="strict-origin-when-cross-origin"
                      allowFullScreen>
                    </iframe>
                  </div>
                </div>
              </div>
            </section>
          }
          imageUrl="/assets/slide3.png"
        />
      </section>

      <CuratedCollections
        title="Curated Bar Collections"
        description="Discover cohesive furniture sets and decor ideas for your bar."
        collections={barCollections}
      />

      {/* Contact Modal */}
      <ContactModal isOpen={showModal} onClose={closeModal} />

    </div>
  );
}

export default Bar;
